import React, { useEffect } from 'react';
import { getColor } from '../../../../assets/theme';
import {
  Tooltip,
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Tabs,
  Tab,
} from '@mui/material';
import { HelpOutline, Close } from '@mui/icons-material';
import { LoadingButton, TabContext, TabPanel } from '@mui/lab';
import AutoCompleteController from '../../../../components/form/controller/autocomplete.controller';
import InputFieldsController from '../../../../components/form/controller/input-fields.controller';
import {
  RoomPostingEntryBody,
  RoomPostingTransactionEntry,
} from '../../interface/room-posting-interface';
import { RoomPostingEntrySchema } from '../../interface/room-posting-schema-interface';
import { usePostTransactionEntriesMutation } from '../../../../api/front-office/roomposting.api.rtk';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import DatePickerController from '../../../../components/form/controller/date-picker.controller';
import { RootState, useAppSelector } from '../../../../store';
import useToast from '../../../../hooks/useToast';
import { useRoomPostingContext } from '../../containers/fo-room_posting.container';
import { useState } from 'react';
import moment from 'moment';
import CustomNumberFormat, {
  CustomNumberFormatProps,
} from '../../../../components/form/custom-number-format';
import useDisclosure from '@hooks/useDisclosure';
import { usePostCASTransactionMutation } from '@api/cas/cas.api.rtk';
import { CASPostTransaction } from '@interfaces/cas.interface';
import CASARVoucher from '@components/voucher/cas-ar-voucher';
import { useCurrentModule } from '@hooks/useCurrentModule';
import { selectFOSalesDate } from '@store/selector/app.selector';

export const optionsBank = [
  'BDO',
  'BPI',
  'China Bank',
  'Landbank',
  'Metrobank',
  'PNB',
  'Security Bank',
  'UnionBank',
  'Others',
];

const RoomPostingPopover = ({
  optionsEntry,
  optionsEntryCategory,
  optionsEntryID,
  optionsEntryAmount,
  onPopOverClose,
  totalPayables,
}: {
  optionsEntry: string;
  optionsEntryCategory: string;
  optionsEntryID: string;
  optionsEntryAmount?: string;
  onPopOverClose: () => void;
  totalPayables?: number;
}) => {
  //#region useAppSelector
  const { selectedProperty } = useAppSelector((state: RootState) => ({
    selectedProperty: state.properties.selectedProperty.value,
  }));

  const moduleSelected = useCurrentModule();
  //#endregion

  const currentSalesDate = useAppSelector(selectFOSalesDate);

  const { primary, textColor, textGray } = getColor();
  const [tabSelect, setTabSelect] = useState(0);
  const [selectedCardType, setSelectedCardType] = useState<string>('Credit');
  const { toast } = useToast();
  const [disabledTabs, setDisabledTabs] = useState([true, true, true]);
  const [isProcessing, setIsProcessing] = useState(false);

  // API Posting CAS
  const [postCASTransactionAPI] = usePostCASTransactionMutation();
  const [casARno, setCASARno] = useState<number>(0);
  const printARDisclosure = useDisclosure();

  const {
    selectedFolioUuid,
    selectedSubGroup,
    ledgerRefetch,
    transactionType,
    roomPostingLedgerList,
  } = useRoomPostingContext();
  const [postTransactionEntries] = usePostTransactionEntriesMutation();

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabSelect(newValue);
  };

  const handleCardTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedCardType((event.target as HTMLInputElement).value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'card_no') {
      const formattedValue = formatCreditCardNumber(value);
      setValue('card_no', formattedValue);
    } else if (name === 'expiration_date') {
      const formattedValue = formatExpirationDate(value);
      setValue('expiration_date', formattedValue, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  function formatCreditCardNumber(value: string | undefined) {
    if (!value) {
      return value;
    }

    const clearValue = clearNumber(value);
    const nextValue = `${clearValue.slice(0, 4)} ${clearValue.slice(
      4,
      8,
    )} ${clearValue.slice(8, 12)} ${clearValue.slice(12, 16)}`;

    return nextValue.trim();
  }

  function formatExpirationDate(value: string | undefined) {
    const clearValue = clearNumber(value);

    if (clearValue.length >= 3) {
      return `${clearValue.slice(0, 2)}/${clearValue.slice(2, 4)}`;
    }

    return clearValue;
  }

  function clearNumber(value = '') {
    return value.replace(/\D+/g, '');
  }

  useEffect(() => {
    const disabledTabsCopy = [true, true, true];
    if (optionsEntryCategory === 'Cheque') {
      setTabSelect(0);
      disabledTabsCopy[0] = false;
    } else if (
      optionsEntryCategory === 'Card' ||
      optionsEntryCategory === 'Bank'
    ) {
      setTabSelect(2);
      disabledTabsCopy[2] = false;
    } else {
      setTabSelect(1);
      disabledTabsCopy[1] = false;
    }

    setDisabledTabs(disabledTabsCopy);
  }, [optionsEntryCategory]);

  useEffect(() => {
    const firstBankOption = optionsBank?.[0];
    if (!firstBankOption) return;

    if (tabSelect === 0) {
      // Check tab
      setValue('issuing_bank', firstBankOption);
    }

    if (tabSelect === 2) {
      // Card Details tab
      setValue('card_network', firstBankOption);
      setValue('cc_issuing_bank', firstBankOption);
    }
  }, [tabSelect]);

  const handlePaymentDetails: SubmitHandler<RoomPostingEntryBody> = async (
    data,
  ) => {
    const transactionIdentifier = selectedFolioUuid;
    const subGroupId = selectedSubGroup;

    const cutOffDate = moment(currentSalesDate).format('YYYY-MM-DD');
    const checkDate = moment(data.check_date).format('YYYY-MM-DD');
    const parsedAmount = parseFloat(
      String(data.gross_amount).replace(/,/g, ''),
    );
    const bookingStatus = roomPostingLedgerList.data?.bookingStatus;
    const isReservation =
      bookingStatus === 'Tentative' || bookingStatus === 'Confirmed';

    if (
      totalPayables &&
      parsedAmount > totalPayables &&
      transactionType === 'Post Master'
    ) {
      toast({
        severity: 'warning',
        message: 'Payment amount cannot exceed total payables.',
        duration: 1200,
      });
      return;
    }

    let paymentDetails = {};
    if (tabSelect === 0) {
      // Check details
      paymentDetails = {
        or_no: data.or_no,
        issuing_bank: data.issuing_bank,
        check_date: checkDate,
        check_number: data.check_no,
      };
    } else if (tabSelect === 1) {
      // Gift Certificate details
      paymentDetails = {
        or_no: data.or_no,
        gift_certificate_number: data.gc_no,
      };
    } else if (tabSelect === 2) {
      // Card details
      paymentDetails = {
        or_no: data.or_no,
        card_network: data.card_network,
        cc_issuing_bank: data.cc_issuing_bank,
        card_number: data.card_no,
        card_holders_name: data.card_holders_name,
        expiration_date: data.expiration_date,
        approval_code: data.approval_code,
        card_type: data.card_type,
      };
    }

    let mappingType = 1;
    switch (transactionType) {
      case 'Room Posting': //Booking
        if (isReservation) {
          // Deposits
          mappingType = 3;
        } else {
          // On Account
          mappingType = 1;
        }
        break;
      case 'Post Master': //City Ledger
        mappingType = 2;
        break;
      case 'Reservation': //Deposits
        mappingType = 3;
        break;
    }
    setIsProcessing(true);

    const jsonBody: RoomPostingTransactionEntry = {
      properties: [
        {
          property: {
            propertyID: selectedProperty,
            entries: [
              {
                entry: {
                  transaction_identifier: transactionIdentifier,
                  cutoff_date: cutOffDate,
                  quantity: data.quantity,
                  gross_amount: parsedAmount,
                  mapping_type: mappingType,
                  module_id: 1,
                  transaction_type: 4,
                  dr_cr_adj: 1,
                  item: optionsEntryID,
                  discount: 0,
                  discount_type: 1,
                  sub_group: subGroupId,
                  category_type: 1,
                  line_remarks: data.line_remarks || '',
                  discount_remarks: '',
                  payment_details: paymentDetails,
                },
              },
            ],
          },
        },
      ],
    };

    //API Call

    try {
      const postEntryResponse = await postTransactionEntries(jsonBody).unwrap();
      let ledger_id = 0;

      if (postEntryResponse?.data?.[0]?.result) {
        ledger_id = postEntryResponse.data[0].result;
      }

      toast({
        severity: 'success',
        message: 'Successfully posted entry.',
        duration: 1000,
      });

      // Post AR for payments if necessary
      if (ledger_id > 0) {
        const cas_id = await postCASTransaction('AR', ledger_id);
        if (cas_id > 0) {
          setCASARno(cas_id);
          printARDisclosure.onOpen();
        }
      } else {
        onPopOverClose();
        ledgerRefetch();
      }
    } catch (err) {
      toast({
        severity: 'error',
        message: err,
      });
    } finally {
      reset();
      setIsProcessing(false);
    }
  };
  const postCASTransaction = async (
    cas_type: string,
    transaction_id: number | string,
  ): Promise<number> => {
    const jsonCASBody: CASPostTransaction = {
      properties: [
        {
          property: {
            propertyID: selectedProperty,
            cas_transaction: [
              {
                module_code: moduleSelected,
                cas_type: cas_type,
                source: 'Room Posting',
                transaction_id: transaction_id,
                guest_id: roomPostingLedgerList?.data?.guestID || 0,
              },
            ],
          },
        },
      ],
    };

    try {
      const casResponse = await postCASTransactionAPI(jsonCASBody).unwrap();
      // Extract the casResponse
      if (casResponse?.data?.[0]?.[0]?.result) {
        return casResponse.data[0][0].result;
      } else {
        return 0;
      }
    } catch (err) {
      toast({ severity: 'error', message: err });
      throw err;
    }
  };

  const methods = useForm<RoomPostingEntryBody>({
    defaultValues: {
      quantity: 1,
      gross_amount: Number(optionsEntryAmount) || 0,
      item: optionsEntry,
      line_remarks: '',
      payment_details: {},
      or_no: '',
      issuing_bank: '',
      check_date: moment(),
      check_no: '',
      gc_no: '',
      card_network: '',
      cc_issuing_bank: '',
      card_no: '',
      card_holders_name: '',
      expiration_date: '',
      approval_code: '',
      card_type: selectedCardType,
    },
    resolver: yupResolver<RoomPostingEntryBody>(
      RoomPostingEntrySchema(tabSelect, false, false),
    ),

    mode: 'onSubmit',
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    // getValues,
    setValue,
    // watch
  } = methods;

  return (
    <>
      <FormProvider {...methods}>
        <Stack
          sx={{
            border: 0,
            color: textGray,
            bgcolor: 'transparent',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
          }}
        >
          <form
            id='payment-details'
            onSubmit={handleSubmit(handlePaymentDetails)}
          >
            <Box sx={{ p: '0 24px 16px 24px', minWidth: '570px' }}>
              <Stack
                direction='row'
                justifyContent='space-between'
                borderBottom='1px solid #EEE'
                p='16px 0'
              >
                <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
                  Entry Posting Window
                  <Tooltip title='Learn More' placement='right-end'>
                    <IconButton
                      sx={{
                        border: 0,
                        color: textGray,
                        bgcolor: 'transparent',
                        ':hover': {
                          color: primary,
                          bgcolor: 'transparent',
                        },
                      }}
                      disableRipple
                    >
                      <HelpOutline />
                    </IconButton>
                  </Tooltip>
                </Typography>
                <IconButton color='inherit' onClick={onPopOverClose}>
                  <Close />
                </IconButton>
              </Stack>
            </Box>

            <Stack flex={1} justifyContent='flex-start'>
              <TabContext value='Default'>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flex: '1,0,0',
                  }}
                ></Box>
                <TabPanel value='Default'>
                  <Stack direction='row'>
                    <InputFieldsController
                      label='Entry Type'
                      name='item'
                      placeholder='Entry Type'
                      control={control}
                      errors={errors}
                      inputProps={{ readOnly: true }}
                      inputStyle={{
                        '.MuiInputBase-root': { width: '100%' },
                      }}
                      borderErrorOnly
                      required
                    />
                  </Stack>
                  <Box mb={1} />
                  <Stack direction={{ sx: 'column', lg: 'row' }} gap={1}>
                    <InputFieldsController
                      label='OR #'
                      name='or_no'
                      placeholder='OR #'
                      control={control}
                      errors={errors}
                      inputStyle={{
                        '.MuiInputBase-root': { width: '100%' },
                      }}
                    />
                    <InputFieldsController
                      label='Amount to Post'
                      name='gross_amount'
                      control={control}
                      errors={errors}
                      inputProps={{
                        style: { textAlign: 'right' },
                      }}
                      inputStyle={{
                        '.MuiInputBase-root': { width: '100%' },
                      }}
                      InputProps={{
                        inputComponent: (props: CustomNumberFormatProps) => (
                          <CustomNumberFormat
                            {...props}
                            allowNegative={false}
                            isNegative={false}
                          />
                        ),
                      }}
                      autoComplete='on'
                      required
                    />
                  </Stack>
                  <Stack direction='row'>
                    <InputFieldsController
                      label='Remarks'
                      name='line_remarks'
                      placeholder=' '
                      control={control}
                      errors={errors}
                      inputProps={{ minLength: 5, maxLength: 100 }}
                      multiline
                      rows={3}
                      inputStyle={{
                        '.MuiInputBase-root': { width: '100%' },
                      }}
                      showTextLength
                    />
                  </Stack>
                  <Typography
                    sx={{ fontSize: '14px', color: textColor, p: '10px' }}
                  >
                    {`Payment Details`}
                  </Typography>
                  <Stack direction='row' sx={{ marginBottom: 3 }}>
                    <Tabs
                      value={tabSelect}
                      onChange={handleChange}
                      textColor='primary'
                      indicatorColor='primary'
                    >
                      <Tab label='Check' disabled={disabledTabs[0]} />
                      <Tab
                        label='Gift Certificate'
                        disabled={disabledTabs[1]}
                      />
                      <Tab label='Card Details' disabled={disabledTabs[2]} />
                    </Tabs>
                  </Stack>
                  <Box mb={1} />
                  {tabSelect === 0 && (
                    <Box sx={{ width: '570px' }}>
                      <Stack direction='row' gap={1}>
                        <AutoCompleteController
                          label='Issuing Bank'
                          name='issuing_bank'
                          options={optionsBank || []}
                          control={control}
                          errors={errors}
                          style={{ width: '100%' }}
                          borderErrorOnly
                        />
                        <DatePickerController
                          control={control}
                          name='check_date'
                          label='Check Date'
                          defaultValue={moment()}
                          style={{ width: '70%' }}
                          disablePast
                        />
                        <InputFieldsController
                          control={control}
                          errors={errors}
                          label='Check #'
                          name='check_no'
                          borderErrorOnly
                          style={{ width: '100%' }}
                        />
                      </Stack>
                    </Box>
                  )}
                  {tabSelect === 1 && (
                    <Box sx={{ width: '570px' }}>
                      <Stack direction='row' gap={1}>
                        <InputFieldsController
                          control={control}
                          errors={errors}
                          label='Gift Certificate #'
                          name='gc_no'
                          borderErrorOnly
                          style={{ width: '100%' }}
                          inputProps={{ maxLength: 30 }}
                        />
                      </Stack>
                    </Box>
                  )}
                  {tabSelect === 2 && (
                    <Box sx={{ width: '570px' }}>
                      <Stack>
                        <Stack direction='row' gap={1}>
                          <AutoCompleteController
                            label='Card Network'
                            name='card_network'
                            options={optionsBank || []}
                            control={control}
                            errors={errors}
                            sx={{
                              width: '100%',
                              mb: errors?.card_network ? 2 : 0,
                            }}
                            borderErrorOnly
                          />
                          <AutoCompleteController
                            label='Issuing Bank'
                            name='cc_issuing_bank'
                            options={optionsBank || []}
                            control={control}
                            errors={errors}
                            sx={{
                              width: '100%',
                              mb: errors?.cc_issuing_bank ? 2 : 0,
                            }}
                            borderErrorOnly
                          />
                          <InputFieldsController
                            control={control}
                            errors={errors}
                            label='Card #'
                            name='card_no'
                            inputProps={{ minLength: 4, maxLength: 4 }}
                            //onChange={handleInputChange}
                            // onKeyDown={(event) => {
                            //   const allowedKeys = [
                            //     'Backspace',
                            //     'Delete',
                            //     'ArrowLeft',
                            //     'ArrowRight',
                            //     'Tab',
                            //   ];

                            //   if (
                            //     !/[0-9]/.test(event.key) &&
                            //     !allowedKeys.includes(event.key)
                            //   ) {
                            //     event.preventDefault();
                            //   }
                            // }}
                            onKeyDown={(event) => {
                              const allowedKeys = [
                                'Backspace',
                                'Delete',
                                'ArrowLeft',
                                'ArrowRight',
                                'Tab',
                              ];

                              if (
                                !/[0-9]/.test(event.key) &&
                                !allowedKeys.includes(event.key)
                              ) {
                                event.preventDefault();
                              }
                            }}
                            borderErrorOnly
                            sx={{
                              width: '100%',
                              mb: errors?.card_no ? 2 : 0,
                            }}
                          />
                        </Stack>
                        <Stack direction='row' gap={1}>
                          <InputFieldsController
                            label='Card Holders Name'
                            name='card_holders_name'
                            control={control}
                            errors={errors}
                            style={{ width: 'calc(100% - -69px)' }}
                            borderErrorOnly
                            inputProps={{ maxLength: 100 }}
                          />
                          <InputFieldsController
                            label='MM/YY'
                            name='expiration_date'
                            control={control}
                            errors={errors}
                            inputProps={{ maxLength: 5 }}
                            onChange={handleInputChange}
                            style={{ width: '62%', marginLeft: '70px' }}
                            borderErrorOnly
                          />
                          <InputFieldsController
                            control={control}
                            errors={errors}
                            label='Approval Code'
                            name='approval_code'
                            inputProps={{ maxLength: 10 }}
                            onKeyDown={(event) => {
                              const allowedKeys = [
                                'Backspace',
                                'Delete',
                                'ArrowLeft',
                                'ArrowRight',
                                'Tab',
                              ];

                              if (
                                !/[0-9]/.test(event.key) &&
                                !allowedKeys.includes(event.key)
                              ) {
                                event.preventDefault();
                              }
                            }}
                            borderErrorOnly
                            style={{ margin: 0, width: '100%' }}
                          />
                        </Stack>
                        <Stack direction='row' gap={2}>
                          <Typography
                            sx={{
                              fontSize: '14px',
                              color: textColor,
                              p: '10px',
                            }}
                          >
                            {`Card Type`}
                          </Typography>
                          <RadioGroup
                            row
                            aria-labelledby='demo-form-control-label-placement'
                            name='card_type'
                            value={selectedCardType}
                            onChange={handleCardTypeChange}
                          >
                            <FormControlLabel
                              value='Credit'
                              control={<Radio />}
                              label='Credit'
                            />
                            <FormControlLabel
                              value='Debit'
                              control={<Radio />}
                              label='Debit'
                            />
                            <FormControlLabel
                              value='Others'
                              control={<Radio />}
                              label='Others'
                            />
                          </RadioGroup>
                          <Button
                            sx={{
                              width: '100px',
                              height: '35px',
                              border: '1px solid',
                              backgroundColor: 'transparent',
                              whiteSpace: 'nowrap',
                              display: 'none', //hide temporary
                            }}
                          >
                            {'Read Card'}
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  )}
                  <Box mb={1} />
                  <Stack
                    direction='row'
                    justifyContent='flex-end'
                    borderBottom='1px solid #EEE'
                  ></Stack>
                  <Box mb={2} />
                  <Stack direction='row' gap={1} justifyContent='flex-end'>
                    <Box flexGrow={1} />{' '}
                    <LoadingButton
                      //type='submit'
                      variant='contained'
                      sx={{
                        width: '100px',
                        height: '42px',
                      }}
                      form='payment-details'
                      disabled={isProcessing}
                      loading={isProcessing}
                      onClick={handleSubmit(handlePaymentDetails)}
                    >
                      Post
                    </LoadingButton>
                    <Button
                      sx={{
                        width: '80px',
                        height: '42px',
                        border: '1px solid',
                        backgroundColor: 'transparent',
                      }}
                      onClick={onPopOverClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </TabPanel>
              </TabContext>
            </Stack>
          </form>
        </Stack>
      </FormProvider>

      <CASARVoucher
        ar_no={casARno}
        isOpen={printARDisclosure.isOpen}
        onClose={() => {
          printARDisclosure.onClose();
          onPopOverClose();
          ledgerRefetch();
        }}
      />
    </>
  );
};

export default RoomPostingPopover;
